"use client";

import { EmptyState, ErrorState } from "@/components/admin/UIStates";
import ThoughtLeadershipCard from "@/components/ui/ThoughtLeadershipCard";
import ThoughtLeadershipCardHero from "@/components/ui/ThoughtLeadershipCardHero";
import ThoughtLeadershipCardHeroSkeleton from "@/components/ui/ThoughtLeadershipCardHeroSkeleton";
import ThoughtLeadershipCardSkeleton from "@/components/ui/ThoughtLeadershipCardSkeleton";
import ContentSearchSidebar from "@/components/ui/ContentSearchSidebar";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { ADMIN_LIST_FILTERS_DEBOUNCE_MS } from "@/lib/constants/adminListUi";
import { ChevronDown, FileText, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
// export const dynamic = "force-dynamic";
interface ThoughtLeadership {
  _id: string;
  title: string;
  slug: string;
  excerpt: string;
  featuredImage?: string;
  author: string;
  tags: string[];
  publishDate: string;
  formattedDate: string;
  readTime: number;
  views: number;
}

interface ThoughtLeadershipResponse {
  thoughtLeadership: ThoughtLeadership[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export default function ThoughtLeadershipPage() {
  const [thoughtLeadership, setThoughtLeadership] = useState<ThoughtLeadership[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreThoughtLeadership, setHasMoreThoughtLeadership] = useState(true);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("All Categories");
  const [postSearch, setPostSearch] = useState<string>("");
  const debouncedPostSearch = useDebouncedValue(
    postSearch,
    ADMIN_LIST_FILTERS_DEBOUNCE_MS
  );

  // Fetch categories from API
  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch('/api/categories', {
        cache: 'no-store', // Always fetch fresh categories
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }

      const data = await response.json();
      if (data.categories && Array.isArray(data.categories)) {
        // Extract category names and sort them
        const categoryNames = data.categories.map((cat: { name: string }) => cat.name).sort();
        setCategories(["All Categories", ...categoryNames]);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      // Keep existing categories if API fails, or set empty if none exist
      setCategories(prev => prev.length > 1 ? prev : ["All Categories"]);
    }
  }, []);

  const fetchThoughtLeadership = useCallback(async (isInitialLoad = false, pageToLoad = 1) => {
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    setError(null);

    try {
      const params = new URLSearchParams({
        page: String(pageToLoad),
        limit: "12",
      });
      if (selectedCategory !== "All Categories") {
        params.set("category", selectedCategory);
      }
      if (debouncedPostSearch.trim()) {
        params.set('search', debouncedPostSearch.trim());
      }

      const response = await fetch(`/api/thought-leadership?${params.toString()}`, {
        cache: 'no-store' // Always fetch fresh thought leadership
      });

      if (!response.ok) {
        throw new Error('Failed to fetch thought leadership');
      }

      const data: ThoughtLeadershipResponse = await response.json();

      // Handle both response structures (thoughtLeadership or blogs)
      const items = data.thoughtLeadership || (data as unknown as { blogs?: ThoughtLeadership[] }).blogs || [];

      if (isInitialLoad) {
        setThoughtLeadership(items);
        setCurrentPage(1);
      } else {
        setThoughtLeadership(prevThoughtLeadership => [...prevThoughtLeadership, ...items]);
        setCurrentPage(pageToLoad);
      }

      setHasMoreThoughtLeadership(pageToLoad < data.pagination.pages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch thought leadership');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [debouncedPostSearch, selectedCategory]);


  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Refresh categories when window regains focus (e.g., after updating categories in admin)
  useEffect(() => {
    const handleFocus = () => {
      fetchCategories();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchCategories]);

  // Fetch thought leadership on mount and when category changes
  useEffect(() => {
    fetchThoughtLeadership(true);
  }, [selectedCategory, fetchThoughtLeadership]);


  const handleViewMore = () => {
    if (!loadingMore && hasMoreThoughtLeadership) {
      fetchThoughtLeadership(false, currentPage + 1);
    }
  };


  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1);
    setHasMoreThoughtLeadership(true);
  };
  const handlePostSearchChange = (value: string) => {
    setPostSearch(value);
    setCurrentPage(1);
    setHasMoreThoughtLeadership(true);
  };

  const hasResults = thoughtLeadership.length > 0;
  const heroItem = hasResults ? thoughtLeadership[0] : undefined;
  const remaining = hasResults ? thoughtLeadership.slice(1) : [];
  const bottomCards = remaining;

  if (loading) {
    return (
      <main className="overflow-hidden">
        <section className="lg:py-24 py-16 relative text-white">
          <div className="custom-container mx-auto">
            {/* Heading Skeleton */}
            <div className="lg:mb-20 mb-5 flex justify-between md:items-center flex-col md:flex-row">
              <div className="animate-pulse">
                <div className="h-8 bg-gray-200 rounded w-64 mb-2"></div>
                <div className="h-12 bg-gray-200 rounded w-96"></div>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 xl:gap-6">
              <div className="xl:col-span-9">
                {/* Hero card skeleton (updated to match new full-width hero layout) */}
                <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
                  <div className="xl:col-span-12">
                    <ThoughtLeadershipCardHeroSkeleton />
                  </div>
                </div>

                {/* Bottom Grid skeleton (show 6 placeholders to approximate content) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                  <ThoughtLeadershipCardSkeleton />
                  <ThoughtLeadershipCardSkeleton />
                  <ThoughtLeadershipCardSkeleton />
                  <ThoughtLeadershipCardSkeleton />
                  <ThoughtLeadershipCardSkeleton />
                  <ThoughtLeadershipCardSkeleton />
                </div>
              </div>

              {/* Categories Filter Column Skeleton */}
              <div className="xl:col-span-3 order-1 xl:order-2">
                <div className="bg-white rounded-lg p-4 xl:p-6 shadow-lg sticky top-4 xl:top-20">
                  <div className="h-6 bg-gray-200 rounded w-24 mb-4 animate-pulse"></div>

                  {/* Search Input Skeleton */}
                  <div className="relative mb-4 xl:mb-6">
                    <div className="h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                  </div>

                  {/* Categories List Skeleton */}
                  <div className="space-y-0 h-[300px] xl:h-[350px] overflow-y-auto custom-scrollbar">
                    {[...Array(8)].map((_, index) => (
                      <div key={index} className="border-b border-gray-100 py-2 xl:py-3 px-2">
                        <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    );
  }

  if (error) {
    return (
      <main>
        <section className="lg:py-24 py-16 relative text-white">
          <div className="custom-container mx-auto">
            <div className="max-w-2xl mx-auto">
              <ErrorState
                error={{
                  type: 'server',
                  message: 'Unable to load thought leadership',
                  details: error,
                  canRetry: true
                }}
                onRetry={() => fetchThoughtLeadership(true)}
              />
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main>
      <section className="lg:py-24 py-16 relative text-white">
        <div className="custom-container mx-auto">
          {/* Heading */}
          <div className="lg:mb-16 mb-5 flex justify-between md:items-center flex-col md:flex-row">

            <div>
             
              <h1 className="tracking-tight leading-tight mb-0 pb-0">
  <span className="block text-2xl lg:text-4xl font-semibold text-[#1b1b1b]">
    Enterprise
  </span>
  <span className="block text-3xl sm:text-4xl xl:text-5xl 2xl:text-6xl font-bold text-[#272d57]">
    Thought Leadership
  </span>
</h1>
            </div>

          </div>

          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 xl:gap-6">
            <div className="xl:col-span-9 order-2 xl:order-1">
              {/* Top Grid: Hero */}
              {hasResults ? (
                <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
                  {/* Hero card */}
                  {heroItem && (
                    <div className="xl:col-span-12 ">
                      <ThoughtLeadershipCardHero
                        image={heroItem.featuredImage || '/api/placeholder/400/250'}
                        date={heroItem.formattedDate}
                        title={heroItem.title}
                        excerpt={heroItem.excerpt}
                        category={heroItem.tags[0] || 'Thought Leadership'}
                        slug={heroItem.slug}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-8">
                  <EmptyState
                    icon={<FileText className="h-16 w-16 text-gray-400" />}
                    title="No thought leadership found"
                    description={
                      selectedCategory !== "All Categories"
                        ? `No thought leadership found in the "${selectedCategory}" category. Try selecting a different category or clearing your search.`
                        : "No thought leadership are available at the moment. Check back later for new content."
                    }
                    action={
                      selectedCategory !== "All Categories" ? (
                        <button
                          onClick={() => handleCategoryChange("All Categories")}
                          className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium"
                        >
                          View All Categories
                        </button>
                      ) : null
                    }
                  />
                </div>
              )}

              {/* Bottom Grid: remaining cards */}
              {hasResults && bottomCards.length > 0 && (
                <div className="grid disabling grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                  {bottomCards.map((card) => (
                    <ThoughtLeadershipCard
                      key={card._id}
                      image={card.featuredImage || '/api/placeholder/400/250'}
                      date={card.formattedDate}
                      category={card.tags[0] || 'Thought Leadership'}
                      title={card.title}
                      excerpt={card.excerpt}
                      slug={card.slug}
                    />
                  ))}
                </div>
              )}

              {/* View More Button */}
              {hasMoreThoughtLeadership && (
                <div className="flex justify-center mt-12">
                  <button
                    onClick={handleViewMore}
                    disabled={loadingMore}
                    className="group relative px-8 py-4 bg-gradient-to-r from-primary to-primary/90 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 active:scale-95"
                  >
                    <div className="flex items-center space-x-3">
                      {loadingMore ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          <span>Loading more thought leadership...</span>
                        </>
                      ) : (
                        <>
                          <span>View More Thought Leadership</span>
                          <ChevronDown className="h-5 w-5 group-hover:translate-y-1 transition-transform duration-200" />
                        </>
                      )}
                    </div>
                  </button>
                </div>
              )}

              {/* Loading More Skeletons */}
              {loadingMore && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                  <ThoughtLeadershipCardSkeleton />
                  <ThoughtLeadershipCardSkeleton />
                  <ThoughtLeadershipCardSkeleton />
                  <ThoughtLeadershipCardSkeleton />
                </div>
              )}
            </div>

            {/* Categories Filter Column */}
            <div className="xl:col-span-3 order-1 xl:order-2">
              <ContentSearchSidebar
                categories={categories}
                selectedCategory={selectedCategory}
                postSearch={postSearch}
                contentLabel="thought leadership"
                postSearchPlaceholder="Search thought leadership..."
                onPostSearchChange={handlePostSearchChange}
                onCategoryChange={handleCategoryChange}
              />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

